import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentTextIcon,
  DocumentIcon,
  BuildingLibraryIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

interface NavigationSection {
  section: string;
  items: {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

const navigation: NavigationSection[] = [
  {
    section: 'Principal',
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon },
    ],
  },
  {
    section: 'Cadastros Base',
    items: [
      { name: 'Cidades', href: '/cidades', icon: MapPinIcon },
      { name: 'Instituições', href: '/instituicoes', icon: BuildingLibraryIcon },
      { name: 'Cursos', href: '/cursos', icon: AcademicCapIcon },
      { name: 'Seguradoras', href: '/seguradoras', icon: ShieldExclamationIcon },
    ],
  },
  {
    section: 'Gestão',
    items: [
      { name: 'Alunos', href: '/alunos', icon: UserGroupIcon },
      { name: 'Empresas', href: '/empresas', icon: BuildingOfficeIcon },
      { name: 'Estágios', href: '/estagios', icon: ClipboardDocumentListIcon },
      { name: 'Horários', href: '/horarios', icon: ClockIcon },
    ],
  },
  {
    section: 'Documentos',
    items: [
      { name: 'Relatórios', href: '/relatorios', icon: DocumentTextIcon },
      { name: 'Termos', href: '/gerar-termo', icon: DocumentIcon },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-center border-b border-gray-200">
            <h1 className="text-xl font-bold text-primary-600">Sistema de Estágio</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            {navigation.map((section, sectionIdx) => (
              <div key={section.section} className={sectionIdx > 0 ? 'mt-6' : ''}>
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {section.section}
                </h3>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                          isActive
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                        onClick={() => {
                          // Close sidebar on mobile when navigating
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                      >
                        <item.icon
                          className={cn(
                            'mr-3 h-5 w-5 flex-shrink-0',
                            isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                          )}
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User info */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-700">U</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">Usuário</p>
                <p className="text-xs text-gray-500">Sistema de Estágio</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
